import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../../common/utils/generalUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { dateRangeOverlap} from "../../common/utils/dateUtils";
import { logout } from "../../common/reducers/initContext";
import { canConfirmRosters, canViewUnconfirmedRosters, isActive } from "./rosterMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { aggregatingFetch } from "../../common/hooks/networkHook";

const key = roster => ensureInteger(roster.rosterid);

// NB: This is it's own slice for consistency with the staff version as unconfirmed rosters have a different set of permissions to regular rosters.

export const rosterUnconfirmedMgrSlice = createSlice({
    name: 'mgrUnconfirmedRosters',
    initialState: {
        data: {},
    },
    reducers: {
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                if (r.hours_confirmed) {
                    delete state.data[key(r)];
                } else {
                    state.data[key(r)] = r
                }
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                        roster.shift_travel = shift.travel;
                    }
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => delete state.data[key(roster)])
        },
        extraReducers: builder => {
            builder.addCase(logout, () => rosterUnconfirmedMgrSlice.getInitialState());
        }
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'roster_one', action);

const visualizeFailure = verb => DefaultFailureAction(verb, 'roster_one');
const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/roster/' + encodeURIComponent(id), data =>rosterUnconfirmedMgrSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/rosters', ids, data => rosterUnconfirmedMgrSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

export const fetchUnconfirmedRostersInRange = (start, end, config) => network => network.post('/api/v2/rosterUnconfirmed', {start, end}, rosterUnconfirmedMgrSlice.actions.addOrUpdate, visualizeFailure('get'))

// #endregion
const selectRawRosters = state => state.mgrUnconfirmedRosters.data;

const selectUnconfirmedRosters = (start, end, config) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => !r.hours_confirmed && isActive(r, config) && dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);
// #region selectors

// #endregion

const listen = {
    rosterAdminCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'roster', roster);
        dispatch(rosterUnconfirmedMgrSlice.actions.addOrUpdate(roster))
    },
    rosterAdminUpdated: (roster, { dispatch }) => dispatch(rosterUnconfirmedMgrSlice.actions.addOrUpdate(roster)),
    rosterAdminRemoved: (roster, { dispatch }) => dispatch(rosterUnconfirmedMgrSlice.actions.remove(roster)),
    shiftAdminUpdated: (shift, { dispatch }) => dispatch(rosterUnconfirmedMgrSlice.actions.updateShift(shift))
}

const options = {
    cleanup: { root: rosterUnconfirmedMgrSlice.name, all: state => Object.values(selectRawRosters(state)), removeAll: rosterUnconfirmedMgrSlice.actions.remove, key },
    condition: (credentials) => canConfirmRosters(credentials) || canViewUnconfirmedRosters(credentials)
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'mgrUnfonfirmedRosterByID' });
registerEntityBasedFetch('roster', rosterInitByID);

export const [useMgrUnconfirmedRostersInRange] = dataHookWithCredentialsFactory(selectUnconfirmedRosters, queryMapFactory(fetchUnconfirmedRostersInRange, 'rosterid', rosterInitByID, listen), options);

const reducer = { name: rosterUnconfirmedMgrSlice.name, reducer: rosterUnconfirmedMgrSlice.reducer }
export default reducer;